import { Card } from "@volca/ui/components/ui/card";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@volca/ui/components/ui/table";
import { Text } from "@volca/ui/components/ui/text";
import { dayjs } from "@/core/utils/date";
import { formatByteSize } from "@/profitability/document-chip";
import { matchedColumnLabel } from "@/profitability/matched-columns";
import type {
  MatchedColumn,
  ProfitabilityDocument,
} from "@/profitability/types";

export const RunDocumentCard = ({
  document,
  slotTitle,
  matches = [],
}: {
  document: ProfitabilityDocument;
  slotTitle: string | null;
  matches?: MatchedColumn[];
}) => {
  const details = [
    slotTitle ? `Used as ${slotTitle}` : null,
    formatByteSize(document.byte_size) || null,
    `Uploaded ${dayjs(document.created_at).format("MMM D, YYYY")}`,
  ].filter(Boolean);

  return (
    <Card
      className="flex min-w-0 flex-col gap-6 border-line p-6 shadow-none"
      data-testid="run-document"
    >
      <Stack gap={0} align="stretch" className="min-w-0">
        <Text variant="title" className="[overflow-wrap:anywhere]">
          {document.file_name}
        </Text>
        <Text tone="muted">{details.join(" · ")}</Text>
      </Stack>
      {document.error && (
        <Text variant="caption" tone="destructive">
          {document.error}
        </Text>
      )}
      {matches.length > 0 && (
        <Stack gap={0} align="stretch" className="min-w-0">
          <Text variant="label">Read under other names</Text>
          {matches.map((match) => (
            <Text
              key={`${match.sheet}:${match.column ?? ""}`}
              tone="muted"
              className="[overflow-wrap:anywhere]"
            >
              {matchedColumnLabel(match)}
            </Text>
          ))}
        </Stack>
      )}
      {document.kind === "text" && <Text tone="muted">Read as text.</Text>}
      {document.sheets.length > 0 && (
        <Stack align="start" gap={4} className="w-full min-w-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Table</TableHead>
                <TableHead className="text-right">Rows</TableHead>
                <TableHead>Columns</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {document.sheets.map((sheet) => (
                <TableRow key={sheet.name}>
                  <TableCell className="whitespace-nowrap">
                    {sheet.name}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {sheet.row_count.toLocaleString()}
                  </TableCell>
                  <TableCell className="max-w-[640px] text-subtle">
                    {sheet.columns.join(", ")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Stack>
      )}
    </Card>
  );
};
